/**
 * Avatar Cropper - Polished Facebook-style image cropping component
 * Features: Pinch-to-zoom, spring physics animations, boundary feedback
 *
 * Usage:
 *   import { createAvatarCropper } from '/shared/avatar-cropper.js';
 *   const cropper = createAvatarCropper({ size: 256 });
 *   cropper.init({ container, image, slider, wrapper });
 *   await cropper.loadImage(file);
 *   const blob = await cropper.getCroppedImage();
 */

// Spring physics configuration
const SPRING = {
    stiffness: 300,
    damping: 25,
    mass: 1
};

// Default options
const DEFAULT_OPTIONS = {
    size: 256,
    minZoom: 1,
    maxZoom: 3,
    quality: 0.9,
    format: 'image/jpeg'
};

/**
 * Calculate distance between two touch points
 */
function getTouchDistance(touch1, touch2) {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Calculate center point between two touches relative to container
 */
function getTouchCenter(touch1, touch2, containerRect) {
    return {
        x: (touch1.clientX + touch2.clientX) / 2 - containerRect.left,
        y: (touch1.clientY + touch2.clientY) / 2 - containerRect.top
    };
}

/**
 * Clamp a value between min and max
 */
function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}

/**
 * Create an avatar cropper instance
 */
export function createAvatarCropper(options = {}) {
    const config = { ...DEFAULT_OPTIONS, ...options };

    // State
    const state = {
        // Position (top-left corner relative to container)
        x: 0,
        y: 0,
        // Current scale
        scale: 1,
        minScale: 1,
        // Original image dimensions
        imgW: 0,
        imgH: 0,
        // Drag state
        dragging: false,
        dragStartX: 0,
        dragStartY: 0,
        dragStartPosX: 0,
        dragStartPosY: 0,
        // Pinch state
        isPinching: false,
        pinchStartDistance: 0,
        pinchStartScale: 1,
        pinchCenterX: 0,
        pinchCenterY: 0,
        // Spring animation
        targetX: 0,
        targetY: 0,
        targetScale: 1,
        velocity: { x: 0, y: 0, scale: 0 },
        animationFrame: null,
        isAnimating: false,
        // Boundary feedback
        boundaryFeedbackActive: false
    };

    // Element references
    let container = null;
    let image = null;
    let slider = null;
    let wrapper = null;
    let _circleBorder = null;

    // Event cleanup
    let cleanupFns = [];

    // ========================================
    // Animation System
    // ========================================

    function animateSpring() {
        if (state.animationFrame) {
            cancelAnimationFrame(state.animationFrame);
        }

        state.isAnimating = true;

        const animate = () => {
            const { stiffness, damping, mass } = SPRING;
            const dt = 1 / 60; // ~60fps

            // Calculate spring forces
            const dx = state.targetX - state.x;
            const dy = state.targetY - state.y;
            const ds = state.targetScale - state.scale;

            // Spring physics: acceleration = (stiffness * displacement - damping * velocity) / mass
            const ax = (stiffness * dx - damping * state.velocity.x) / mass;
            const ay = (stiffness * dy - damping * state.velocity.y) / mass;
            const as = (stiffness * ds - damping * state.velocity.scale) / mass;

            // Update velocity
            state.velocity.x += ax * dt;
            state.velocity.y += ay * dt;
            state.velocity.scale += as * dt;

            // Update position
            state.x += state.velocity.x * dt;
            state.y += state.velocity.y * dt;
            state.scale += state.velocity.scale * dt;

            // Apply constraints
            constrainPosition();
            applyTransform();

            // Check if settled
            const positionSettled = Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5;
            const scaleSettled = Math.abs(ds) < 0.001;
            const velocitySettled =
                Math.abs(state.velocity.x) < 0.5 &&
                Math.abs(state.velocity.y) < 0.5 &&
                Math.abs(state.velocity.scale) < 0.001;

            if (positionSettled && scaleSettled && velocitySettled) {
                // Snap to target
                state.x = state.targetX;
                state.y = state.targetY;
                state.scale = state.targetScale;
                state.velocity = { x: 0, y: 0, scale: 0 };
                state.isAnimating = false;
                constrainPosition();
                applyTransform();
            } else {
                state.animationFrame = requestAnimationFrame(animate);
            }
        };

        state.animationFrame = requestAnimationFrame(animate);
    }

    function stopAnimation() {
        if (state.animationFrame) {
            cancelAnimationFrame(state.animationFrame);
            state.animationFrame = null;
        }
        state.isAnimating = false;
        state.velocity = { x: 0, y: 0, scale: 0 };
    }

    // ========================================
    // Transform & Constraints
    // ========================================

    function applyTransform() {
        if (!image) return;
        image.style.transform = `translate(${state.x}px, ${state.y}px) scale(${state.scale})`;
    }

    function constrainPosition() {
        const scaledW = state.imgW * state.scale;
        const scaledH = state.imgH * state.scale;
        const { size } = config;

        // Image must cover the entire container
        const minX = size - scaledW;
        const maxX = 0;
        const minY = size - scaledH;
        const maxY = 0;

        let hitBoundary = false;

        // Constrain X
        if (state.x > maxX) {
            state.x = maxX;
            state.targetX = maxX;
            hitBoundary = true;
        } else if (state.x < minX) {
            state.x = minX;
            state.targetX = minX;
            hitBoundary = true;
        }

        // Constrain Y
        if (state.y > maxY) {
            state.y = maxY;
            state.targetY = maxY;
            hitBoundary = true;
        } else if (state.y < minY) {
            state.y = minY;
            state.targetY = minY;
            hitBoundary = true;
        }

        // Trigger boundary feedback
        if (hitBoundary && !state.boundaryFeedbackActive && (state.dragging || state.isPinching)) {
            triggerBoundaryFeedback();
        }
    }

    function triggerBoundaryFeedback() {
        state.boundaryFeedbackActive = true;

        // Visual feedback on wrapper
        if (wrapper) {
            wrapper.classList.add('boundary-hit');
        }

        // Haptic feedback
        if ('vibrate' in navigator) {
            navigator.vibrate(10);
        } else if (typeof window.triggerHaptic === 'function') {
            window.triggerHaptic();
        }

        setTimeout(() => {
            wrapper?.classList.remove('boundary-hit');
            state.boundaryFeedbackActive = false;
        }, 150);
    }

    // ========================================
    // Drag Handling
    // ========================================

    function startDrag(clientX, clientY) {
        stopAnimation();
        state.dragging = true;
        state.dragStartX = clientX;
        state.dragStartY = clientY;
        state.dragStartPosX = state.x;
        state.dragStartPosY = state.y;
        container?.classList.add('dragging');
    }

    function moveDrag(clientX, clientY) {
        if (!state.dragging) return;

        const dx = clientX - state.dragStartX;
        const dy = clientY - state.dragStartY;

        state.targetX = state.dragStartPosX + dx;
        state.targetY = state.dragStartPosY + dy;

        // Direct update for responsive feel during drag
        state.x = state.targetX;
        state.y = state.targetY;

        constrainPosition();
        applyTransform();
    }

    function endDrag() {
        state.dragging = false;
        container?.classList.remove('dragging');

        // Settle with spring animation
        state.targetX = state.x;
        state.targetY = state.y;
        state.targetScale = state.scale;
    }

    // ========================================
    // Pinch-to-Zoom Handling
    // ========================================

    function startPinch(touch1, touch2) {
        stopAnimation();
        state.isPinching = true;
        state.pinchStartDistance = getTouchDistance(touch1, touch2);
        state.pinchStartScale = state.scale;

        // Get container rect for center calculation
        const rect = container?.getBoundingClientRect();
        if (rect) {
            const center = getTouchCenter(touch1, touch2, rect);
            state.pinchCenterX = center.x;
            state.pinchCenterY = center.y;
        }

        container?.classList.add('pinching');
    }

    function movePinch(touch1, touch2) {
        if (!state.isPinching) return;

        const currentDistance = getTouchDistance(touch1, touch2);
        const scaleRatio = currentDistance / state.pinchStartDistance;

        // Calculate new scale
        let newScale = state.pinchStartScale * scaleRatio;
        newScale = clamp(newScale, state.minScale, state.minScale * config.maxZoom);

        // Get current pinch center
        const rect = container?.getBoundingClientRect();
        if (rect) {
            const center = getTouchCenter(touch1, touch2, rect);

            // Calculate what image point is at the pinch center
            const imgPointX = (center.x - state.x) / state.scale;
            const imgPointY = (center.y - state.y) / state.scale;

            // New position keeps that image point at pinch center
            state.targetScale = newScale;
            state.targetX = center.x - imgPointX * newScale;
            state.targetY = center.y - imgPointY * newScale;

            // Direct update for responsive feel
            state.scale = newScale;
            state.x = state.targetX;
            state.y = state.targetY;

            constrainPosition();
            applyTransform();
            syncSlider();
        }
    }

    function endPinch() {
        state.isPinching = false;
        container?.classList.remove('pinching');

        // Update targets
        state.targetX = state.x;
        state.targetY = state.y;
        state.targetScale = state.scale;
    }

    // ========================================
    // Slider/Zoom
    // ========================================

    function syncSlider() {
        if (!slider) return;
        // Map scale to slider value (100-300)
        const value = (state.scale / state.minScale) * 100;
        slider.value = clamp(value, 100, 300);
        updateSliderFill();
    }

    function updateSliderFill() {
        if (!slider) return;
        const percent = ((slider.value - 100) / 200) * 100;
        slider.style.setProperty('--zoom-progress', `${percent}%`);
    }

    function handleZoomSlider(value) {
        stopAnimation();

        const { size } = config;
        const newScale = state.minScale * (value / 100);

        // Zoom centered on crop area center
        const centerX = size / 2;
        const centerY = size / 2;

        // Image point at center
        const imgPointX = (centerX - state.x) / state.scale;
        const imgPointY = (centerY - state.y) / state.scale;

        // Set targets
        state.targetScale = newScale;
        state.targetX = centerX - imgPointX * newScale;
        state.targetY = centerY - imgPointY * newScale;

        // Animate to new position
        animateSpring();
        updateSliderFill();
    }

    // ========================================
    // Event Setup
    // ========================================

    function setupEvents() {
        if (!container) return;

        // Mouse events
        const onMouseDown = (e) => {
            e.preventDefault();
            startDrag(e.clientX, e.clientY);
        };

        const onMouseMove = (e) => {
            moveDrag(e.clientX, e.clientY);
        };

        const onMouseUp = () => {
            endDrag();
        };

        container.addEventListener('mousedown', onMouseDown);
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);

        cleanupFns.push(() => {
            container.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        });

        // Touch events
        const onTouchStart = (e) => {
            if (e.touches.length === 2) {
                // Start pinch
                e.preventDefault();
                if (state.dragging) endDrag();
                startPinch(e.touches[0], e.touches[1]);
            } else if (e.touches.length === 1 && !state.isPinching) {
                // Start drag
                e.preventDefault();
                startDrag(e.touches[0].clientX, e.touches[0].clientY);
            }
        };

        const onTouchMove = (e) => {
            if (e.touches.length === 2 && state.isPinching) {
                e.preventDefault();
                movePinch(e.touches[0], e.touches[1]);
            } else if (e.touches.length === 1 && state.dragging) {
                e.preventDefault();
                moveDrag(e.touches[0].clientX, e.touches[0].clientY);
            }
        };

        const onTouchEnd = (e) => {
            if (state.isPinching && e.touches.length < 2) {
                endPinch();
                // If one finger remains, start drag
                if (e.touches.length === 1) {
                    startDrag(e.touches[0].clientX, e.touches[0].clientY);
                }
            } else if (state.dragging && e.touches.length === 0) {
                endDrag();
            }
        };

        container.addEventListener('touchstart', onTouchStart, { passive: false });
        container.addEventListener('touchmove', onTouchMove, { passive: false });
        container.addEventListener('touchend', onTouchEnd);
        container.addEventListener('touchcancel', onTouchEnd);

        cleanupFns.push(() => {
            container.removeEventListener('touchstart', onTouchStart);
            container.removeEventListener('touchmove', onTouchMove);
            container.removeEventListener('touchend', onTouchEnd);
            container.removeEventListener('touchcancel', onTouchEnd);
        });

        // Slider events
        if (slider) {
            const onSliderInput = (e) => {
                handleZoomSlider(parseFloat(e.target.value));
            };
            slider.addEventListener('input', onSliderInput);
            cleanupFns.push(() => slider.removeEventListener('input', onSliderInput));
        }
    }

    // ========================================
    // Public API
    // ========================================

    return {
        /**
         * Initialize the cropper with DOM elements
         */
        init(elements) {
            container = elements.container;
            image = elements.image;
            slider = elements.slider;
            wrapper = elements.wrapper;
            _circleBorder = elements.circleBorder;

            setupEvents();
        },

        /**
         * Load an image file and prepare for cropping
         */
        loadImage(file) {
            return new Promise((resolve, reject) => {
                if (!image) {
                    reject(new Error('Cropper not initialized'));
                    return;
                }

                // Reset state
                stopAnimation();
                state.dragging = false;
                state.isPinching = false;
                state.velocity = { x: 0, y: 0, scale: 0 };

                // Hide image while loading - use visibility to prevent layout issues
                image.style.opacity = '0';
                image.style.transition = 'none';

                const reader = new FileReader();
                reader.onload = (e) => {
                    image.src = e.target.result;
                    image.onload = () => {
                        const { naturalWidth: w, naturalHeight: h } = image;
                        state.imgW = w;
                        state.imgH = h;

                        // Calculate scale to cover the container (image must fill the circle)
                        const scaleToFit = Math.max(config.size / w, config.size / h);
                        state.minScale = scaleToFit;
                        state.scale = scaleToFit;
                        state.targetScale = scaleToFit;

                        // Center the image within the crop area
                        const scaledW = w * scaleToFit;
                        const scaledH = h * scaleToFit;
                        state.x = (config.size - scaledW) / 2;
                        state.y = (config.size - scaledH) / 2;
                        state.targetX = state.x;
                        state.targetY = state.y;

                        // Reset slider
                        if (slider) {
                            slider.min = 100;
                            slider.max = 300;
                            slider.value = 100;
                            updateSliderFill();
                        }

                        // Apply the centered transform immediately (no transition)
                        applyTransform();

                        // Use double RAF to ensure transform is painted before fading in
                        requestAnimationFrame(() => {
                            requestAnimationFrame(() => {
                                // Now fade in the image (only opacity, no transform animation)
                                image.style.transition = 'opacity 0.25s ease-out';
                                image.style.opacity = '1';

                                // Clear transition after animation
                                setTimeout(() => {
                                    image.style.transition = '';
                                    resolve();
                                }, 250);
                            });
                        });
                    };
                    image.onerror = reject;
                };
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
        },

        /**
         * Get the cropped image as a blob
         */
        getCroppedImage() {
            return new Promise((resolve, reject) => {
                if (!image || !state.imgW) {
                    reject(new Error('No image loaded'));
                    return;
                }

                const { x, y, scale, imgW, imgH } = state;
                const { size } = config;

                // Calculate visible region in original image coordinates
                const srcX = Math.max(0, -x / scale);
                const srcY = Math.max(0, -y / scale);
                const srcW = Math.min(imgW - srcX, size / scale);
                const srcH = Math.min(imgH - srcY, size / scale);

                // Create output canvas
                const canvas = document.createElement('canvas');
                canvas.width = size;
                canvas.height = size;
                const ctx = canvas.getContext('2d');

                // Draw the cropped region
                ctx.drawImage(image, srcX, srcY, srcW, srcH, 0, 0, size, size);

                // Convert to blob
                canvas.toBlob((blob) => resolve(blob), config.format, config.quality);
            });
        },

        /**
         * Reset the cropper to initial state
         */
        reset() {
            stopAnimation();
            state.dragging = false;
            state.isPinching = false;

            if (state.imgW && state.imgH) {
                const scaleToFit = Math.max(config.size / state.imgW, config.size / state.imgH);
                state.scale = scaleToFit;
                state.targetScale = scaleToFit;

                const scaledW = state.imgW * scaleToFit;
                const scaledH = state.imgH * scaleToFit;
                state.x = (config.size - scaledW) / 2;
                state.y = (config.size - scaledH) / 2;
                state.targetX = state.x;
                state.targetY = state.y;

                if (slider) {
                    slider.value = 100;
                    updateSliderFill();
                }

                applyTransform();
            }
        },

        /**
         * Clean up event listeners
         */
        destroy() {
            stopAnimation();
            cleanupFns.forEach((fn) => fn());
            cleanupFns = [];
            container = null;
            image = null;
            slider = null;
            wrapper = null;
        },

        /**
         * Get current state (for debugging)
         */
        getState() {
            return { ...state };
        }
    };
}

/**
 * Convert blob to base64 data URL
 */
export function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

// Export utilities for advanced usage
export { getTouchDistance, getTouchCenter, clamp };
